import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { Apps, BarChart as BarChartIcon, Crop, PhotoLibrary, Palette } from '@mui/icons-material';
import * as api from '../../services/api';
import LiteGalleryGrid from '../shared/LiteGalleryGrid';
import MPhotoSectionTabs, { ADMIN_GALLERY_FILTER_STICKY_TOP } from '../shared/MPhotoSectionTabs';
import GalleryStatsPanel from '../shared/GalleryStatsPanel';
import AdminIcons from './Icons';
import CustomFrames from './CustomFrames';
import ThemeList from '../Theme/ThemeList';
import { useI18n } from '../../i18n/useI18n';

interface AdminMPhotoLiteProps {
  userId: string;
}

type LiteTab = 'gallery' | 'icons' | 'custom-frames' | 'themes' | 'stats';

const AdminMPhotoLite: React.FC<AdminMPhotoLiteProps> = ({ userId }) => {
  const { pages: t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: LiteTab =
    tabParam === 'icons'
      ? 'icons'
      : tabParam === 'custom-frames'
        ? 'custom-frames'
        : tabParam === 'themes'
          ? 'themes'
          : tabParam === 'stats'
            ? 'stats'
            : 'gallery';

  const [mountedTabs, setMountedTabs] = useState<Set<LiteTab>>(() => new Set([activeTab]));

  useEffect(() => {
    setMountedTabs((prev) => {
      if (prev.has(activeTab)) return prev;
      const next = new Set(prev);
      next.add(activeTab);
      return next;
    });
  }, [activeTab]);

  const setActiveTab = (tab: LiteTab) => {
    if (tab === 'gallery') {
      searchParams.delete('tab');
    } else {
      searchParams.set('tab', tab);
    }
    setSearchParams(searchParams, { replace: true });
  };

  const tabOptions = useMemo(
    () => [
      { value: 'gallery' as const, label: t.mPhotoLite.tabs.gallery, icon: <PhotoLibrary fontSize="inherit" /> },
      {
        value: 'custom-frames' as const,
        label: t.mPhotoLite.tabs.customFrames,
        shortLabel: t.mPhotoLite.tabs.customFramesShort,
        icon: <Crop fontSize="inherit" />,
      },
      {
        value: 'themes' as const,
        label: t.mPhotoLite.tabs.themes,
        shortLabel: t.mPhotoLite.tabs.themesShort,
        icon: <Palette fontSize="inherit" />,
      },
      { value: 'icons' as const, label: t.mPhotoLite.tabs.icons, icon: <Apps fontSize="inherit" /> },
      { value: 'stats' as const, label: t.mPhotoLite.tabs.stats, icon: <BarChartIcon fontSize="inherit" /> },
    ],
    [t],
  );

  const fetchPage = useCallback(
    (page: number, limit: number, filter?: { machineCode?: string; startDate?: string; endDate?: string }) =>
      api.getAdminUserLitePhotoGroups(userId, page, limit, filter),
    [userId],
  );
  const handleDelete = useCallback(
    (folderId: string) => api.adminDeleteUserLitePhotoGroup(userId, folderId),
    [userId],
  );
  const loadMachines = useCallback(async () => {
    const res = await api.getAdminUserMachines(userId);
    return res.machines || [];
  }, [userId]);

  return (
    <Box>
      <MPhotoSectionTabs
        value={activeTab}
        onChange={setActiveTab}
        options={tabOptions}
        accentColor="#0f3460"
        sticky
      />

      {mountedTabs.has('gallery') && (
        <Box sx={{ display: activeTab === 'gallery' ? 'block' : 'none' }}>
          <LiteGalleryGrid
            fetchPage={fetchPage}
            purgeExpired={() => api.adminPurgeExpiredGalleries(userId, 'lite')}
            purgeExpiredKey={`admin:lite:${userId}`}
            purgeToast
            onDelete={handleDelete}
            emptyMessage="User chưa có gallery M-Photo Lite trên server."
            deviceProduct="lite"
            loadMachines={loadMachines}
            accentColor="#0f3460"
            filterStickyTop={ADMIN_GALLERY_FILTER_STICKY_TOP}
          />
        </Box>
      )}

      {mountedTabs.has('custom-frames') && (
        <Box sx={{ display: activeTab === 'custom-frames' ? 'block' : 'none' }}>
          <CustomFrames userId={userId} channel="lite" variant="user" embedded />
        </Box>
      )}

      {mountedTabs.has('themes') && (
        <Box sx={{ display: activeTab === 'themes' ? 'block' : 'none' }}>
          <ThemeList userId={userId} channel="lite" embedded />
        </Box>
      )}

      {mountedTabs.has('icons') && (
        <Box sx={{ display: activeTab === 'icons' ? 'block' : 'none' }}>
          <AdminIcons userId={userId} channel="lite" />
        </Box>
      )}

      {mountedTabs.has('stats') && (
        <Box sx={{ display: activeTab === 'stats' ? 'block' : 'none' }}>
          <GalleryStatsPanel channel="lite" userId={userId} accentColor="#0f3460" />
        </Box>
      )}
    </Box>
  );
};

export default AdminMPhotoLite;
